import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { OrganizationQuery } from '@core/organization/organization.query';
import { OrganizationType } from '@core/models/organization.model';
import { OrganizationContextService } from '@core/context/organization-context.service';
import { AuthorizationService } from './authorization.service';
import { productRoute, routeParams } from '@shared/shared-routes';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

/**
 * Issuers (emittent orgs) must not access LP IM flows. Deep links redirect to DD for the same product.
 * Investors using fund-ddq-runs keep IM access (their chosen org type is not emittent).
 */
@Injectable({
  providedIn: 'root',
})
export class NoInvestmentMemorandumForIssuerGuard {
  constructor(
    private readonly router: Router,
    private readonly organizationQuery: OrganizationQuery,
    private readonly authorization: AuthorizationService,
    private readonly organizationContextService: OrganizationContextService,
  ) {}

  public canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const productId = this.resolveProductId(route);
    const targetQueryParams = this.collectQueryParamsFromSnapshot(route);

    if (targetQueryParams['context'] === 'fund-ddq-runs') {
      const existingContext = this.organizationContextService.getContextValue();
      this.organizationContextService.setContext({
        context: 'fund-ddq-runs',
        viewAs: (targetQueryParams['viewAs'] as 'investor' | 'emittent' | 'client') || 'investor',
        serviceType: 'emittent',
        issuerUid: targetQueryParams['issuerUid'] || targetQueryParams['issuerid'] || existingContext?.issuerUid,
        ...(existingContext?.ddqProductUid ? { ddqProductUid: existingContext.ddqProductUid } : {}),
      });
    }

    return combineLatest([
      this.organizationQuery.selectChosenOrganizationType().pipe(filter((t): t is OrganizationType => t != null)),
      this.organizationContextService.getContext(),
    ]).pipe(
      take(1),
      map(([type, context]) => {
        if (!this.authorization.isEmittentCompany(type)) {
          return true;
        }
        if (
          context?.context === 'fund-ddq-runs' &&
          (context.viewAs === 'investor' || context.viewAs === 'client')
        ) {
          return true;
        }
        if (!productId) {
          return this.router.parseUrl(`/${productRoute}`);
        }
        return this.router.createUrlTree(['/', productRoute, productId, 'due-diligence'], {
          queryParams: targetQueryParams,
        });
      }),
    );
  }

  private resolveProductId(route: ActivatedRouteSnapshot): string | null {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      const id = current.paramMap.get(routeParams.productId);
      if (id) {
        return id;
      }
      current = current.parent;
    }
    return null;
  }

  private collectQueryParamsFromSnapshot(route: ActivatedRouteSnapshot): Record<string, string> {
    const out: Record<string, string> = {};
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      Object.assign(out, current.queryParams as Record<string, string>);
      current = current.parent;
    }
    return out;
  }
}
